import { useTolgee } from '@tolgee/react';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { storePreferredLocale } from '../../../hooks/useLocale';
import {
  localeOptionsForSelector,
  normalizeLocaleForSelector,
} from '../../../utils/languageSelector';
import { ChevIco, GlobeIco, TickIco } from './AuthIcons';
import { shadowFloat } from './authStyles';

/**
 * Lightweight language switcher for the logged-out auth screen. Unlike the
 * app-wide `LangSelector`, it does NOT write the profile (no authenticated
 * user yet) — it only changes the Tolgee language and stores the preference
 * locally so the choice survives into the session.
 */
const AuthLangSwitcher: React.FC = () => {
  const tolgee = useTolgee(['language']);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const current = tolgee.getLanguage();
  const selectorLocale = current ? normalizeLocaleForSelector(current) : undefined;
  const currentLabel =
    localeOptionsForSelector.find(([value]) => value === selectorLocale)?.[1] ?? '';

  const select = (locale: string) => {
    storePreferredLocale(locale);
    tolgee.changeLanguage(locale);
    setOpen(false);
  };

  return (
    <Wrap ref={ref} $open={open}>
      <Menu role="menu" $open={open}>
        {localeOptionsForSelector.map(([value, label]) => (
          <Opt
            key={value}
            role="menuitem"
            $active={value === selectorLocale}
            onClick={() => select(value)}
          >
            <span>{label}</span>
            {value === selectorLocale && <TickIco />}
          </Opt>
        ))}
      </Menu>
      <Trigger onClick={() => setOpen(o => !o)} aria-haspopup="true" aria-expanded={open}>
        <GlobeIco />
        <span>{currentLabel}</span>
        <ChevIco />
      </Trigger>
    </Wrap>
  );
};

const Wrap = styled.div<{ $open: boolean }>`
  position: relative;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border-radius: 999px;
  font-family: inherit;
  color: ${theme.colors.greyDark};
  font-size: 13px;
  font-weight: 800;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: rgba(40, 47, 51, 0.06);
    color: ${theme.colors.black};
  }
  svg {
    width: 16px;
    height: 16px;
    opacity: 0.8;
  }
  .chev {
    width: 11px;
    height: 11px;
  }
`;

const Menu = styled.div<{ $open: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 172px;
  background: #fff;
  border-radius: 10px;
  box-shadow: ${shadowFloat};
  padding: 6px;
  display: flex;
  flex-direction: column;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? 0 : '6px')});
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: transform 0.18s cubic-bezier(0.645, 0.045, 0.355, 1), opacity 0.18s;
  z-index: 20;
`;

const Opt = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border-radius: 7px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $active }) => ($active ? theme.colors.orangeDark : theme.colors.black)};
  text-align: left;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.background};
  }
  .tick {
    margin-left: auto;
    width: 14px;
    height: 14px;
    color: ${theme.colors.orange};
  }
`;

export default AuthLangSwitcher;
